import React, { useEffect, useState } from "react";
import { Badge, ProgressBar, Modal, Form, Button, Spinner } from "react-bootstrap";
import api from "../../api";
import { toast } from "react-toastify";
import { LoadingState } from "../../components/ui/LoadingState";
import { ErrorState } from "../../components/ui/ErrorState";
import {
  PHASE_STATUS_META,
  MILESTONE_STATUS_META,
  statusMeta,
  formatDateOrDash,
} from "./projectWorkspaceUtils";

// "Configure Process" modal -- lets the (assumed) project owner/manager/
// Admin pick a Project Type template to seed this project's phases/
// milestones from, via POST /projects/:id/process/initialize
// { project_type_id }. Per the permissions note in the task spec, this is
// shown unconditionally rather than hidden behind a permission this app
// has no reliable client-side way to evaluate (per-project owner/manager
// status) -- the server enforces the real check and a 403 surfaces as a
// toast exactly like every other gated action in this app.
function ConfigureProcessModal({ show, projectId, onClose, onConfigured }) {
  const [projectTypes, setProjectTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) return;
    setSelected("");
    setLoading(true);
    api
      .get("/project-types", { params: { is_active: 1, pageSize: 200 } })
      .then((res) => setProjectTypes(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => setProjectTypes([]))
      .finally(() => setLoading(false));
  }, [show]);

  const handleSave = async () => {
    if (!selected) {
      toast.warning("Select a project type first");
      return;
    }
    try {
      setSaving(true);
      await api.post(`/projects/${projectId}/process/initialize`, { project_type_id: Number(selected) });
      toast.success("Project process configured");
      onConfigured();
    } catch (err) {
      const data = err.response?.data;
      if (err.response?.status === 409) {
        toast.error("This project's process is already configured.");
      } else {
        toast.error(data?.message || "Failed to configure process");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Configure Project Process</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small">
          Choose a Project Type. Its phase/milestone template will be copied into this project as its own,
          independently editable process.
        </p>
        {loading ? (
          <LoadingState message="Loading project types…" />
        ) : (
          <Form.Select value={selected} onChange={(e) => setSelected(e.target.value)}>
            <option value="">Select project type…</option>
            {projectTypes.map((pt) => (
              <option key={pt.id} value={pt.id}>{pt.name}</option>
            ))}
          </Form.Select>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button className="unit-btn unit-btn-primary" onClick={handleSave} disabled={saving || !selected}>
          {saving ? <><Spinner animation="border" size="sm" className="me-2" />Configuring…</> : "Configure Process"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// Schedule chip text. Prefers the server's `schedule_label` ("Completed On
// Time", "Completed Late by N days", "Overdue by N days", "Completed", or
// null); the fallback applies the same rules to `delay` for an API that
// doesn't send it yet -- a COMPLETED phase with no recorded actual end date
// never gets a delay figure.
function scheduleLabel(phase) {
  if (phase.schedule_label !== undefined) return phase.schedule_label;
  const days = phase.delay?.days;
  const plural = (n) => `${n} day${n === 1 ? "" : "s"}`;
  if (phase.status === "COMPLETED") {
    if (!phase.actual_end_date || !phase.delay?.final) return "Completed";
    return days > 0 ? `Completed Late by ${plural(days)}` : "Completed On Time";
  }
  return days > 0 ? `Overdue by ${plural(days)}` : null;
}

const SCHEDULE_CHIP_STYLE = {
  late: { background: "#FCEAEC", color: "#DC4C5A" },
  onTime: { background: "#E8F5EE", color: "#1E7B4F" },
};

function PhaseCard({ phase }) {
  const [open, setOpen] = useState(false);
  const meta = statusMeta(PHASE_STATUS_META, phase.status);
  const schedule = scheduleLabel(phase);
  // Plain "Completed" would just repeat the status badge.
  const showSchedule = !!schedule && schedule !== "Completed";
  const hasActual = !!(phase.actual_start_date || phase.actual_end_date);
  const toggle = () => setOpen((o) => !o);

  return (
    <div className="pw-phase-card">
      <div
        className="pw-phase-card-header"
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggle();
          }
        }}
      >
        <div className="pw-phase-card-header-left">
          <span className="pw-phase-seq">{phase.sequence ?? "-"}</span>
          <div className="pw-phase-title-block">
            <div className="pw-phase-title-line">
              <strong>{phase.name}</strong>
              <Badge bg={meta.tone}>{meta.label}</Badge>
              {showSchedule && (
                <span
                  className="pw-chip"
                  style={schedule === "Completed On Time" ? SCHEDULE_CHIP_STYLE.onTime : SCHEDULE_CHIP_STYLE.late}
                >
                  <i className="fa-solid fa-clock" aria-hidden="true"></i>
                  {schedule}
                </span>
              )}
            </div>
            <div className="pw-phase-dates">
              <span>
                <span className="pw-phase-dates-label">Planned:</span>{" "}
                {formatDateOrDash(phase.planned_start_date)} – {formatDateOrDash(phase.planned_end_date)}
              </span>
              {hasActual && (
                <span>
                  <span className="pw-phase-dates-label">Actual:</span>{" "}
                  {formatDateOrDash(phase.actual_start_date)} – {phase.actual_end_date ? formatDateOrDash(phase.actual_end_date) : "ongoing"}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="d-flex align-items-center gap-3">
          {phase.progress === null || phase.progress === undefined ? (
            <span className="pw-no-tasks-note">No tasks added</span>
          ) : (
            <div style={{ minWidth: 140 }} className="d-flex align-items-center gap-2">
              <ProgressBar now={phase.progress} style={{ width: 100 }} />
              <span className="small fw-semibold">{Math.round(phase.progress)}%</span>
            </div>
          )}
          <i className={`fa-solid ${open ? "fa-chevron-up" : "fa-chevron-down"}`} aria-hidden="true"></i>
        </div>
      </div>

      {open && (
        <div className="pw-phase-card-body">
          <div className="pw-phase-detail-grid">
            <div><span>Owner</span><strong>{phase.owner_name || "Unassigned"}</strong></div>
            <div><span>Planned Start</span><strong>{formatDateOrDash(phase.planned_start_date)}</strong></div>
            <div><span>Planned End</span><strong>{formatDateOrDash(phase.planned_end_date)}</strong></div>
            <div><span>Actual Start</span><strong>{formatDateOrDash(phase.actual_start_date)}</strong></div>
            <div><span>Actual End</span><strong>{formatDateOrDash(phase.actual_end_date)}</strong></div>
            <div><span>Tickets</span><strong>{phase.ticket_counts?.completed ?? 0} / {phase.ticket_counts?.eligible ?? 0} done</strong></div>
          </div>

          <div className="fw-semibold small mb-2">Milestones</div>
          {Array.isArray(phase.milestones) && phase.milestones.length > 0 ? (
            <ul className="pw-milestone-mini-list">
              {phase.milestones.map((m) => {
                const mMeta = statusMeta(MILESTONE_STATUS_META, m.status);
                return (
                  <li key={m.id}>
                    <span>{m.name}</span>
                    <span className="d-flex align-items-center gap-2">
                      <span className="text-muted">{formatDateOrDash(m.planned_date)}</span>
                      <Badge bg={mMeta.tone}>{mMeta.label}</Badge>
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-muted small mb-0">No milestones for this phase.</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function ProjectWorkspaceProcessTab({ projectId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showConfigure, setShowConfigure] = useState(false);

  const load = () => {
    setLoading(true);
    setError("");
    api
      .get(`/projects/${projectId}/process`)
      .then((res) => setData(res.data?.data || null))
      .catch((err) => setError(err.response?.data?.message || "Failed to load project process"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <LoadingState message="Loading process…" />;
  if (error) return <ErrorState message={error} action={<Button onClick={load}>Retry</Button>} />;

  if (!data?.configured) {
    return (
      <div className="pw-not-configured">
        <i className="fa-solid fa-diagram-project" aria-hidden="true"></i>
        <h5>Project process not configured</h5>
        <p className="text-muted">This project has no phase/milestone structure yet.</p>
        <Button className="unit-btn unit-btn-primary" onClick={() => setShowConfigure(true)}>
          <i className="fa-solid fa-gear" aria-hidden="true"></i> Configure Process
        </Button>
        <ConfigureProcessModal
          show={showConfigure}
          projectId={projectId}
          onClose={() => setShowConfigure(false)}
          onConfigured={() => {
            setShowConfigure(false);
            load();
          }}
        />
      </div>
    );
  }

  return (
    <div>
      {data.phases.length === 0 ? (
        <p className="text-muted">No phases defined.</p>
      ) : (
        data.phases.map((phase) => <PhaseCard key={phase.id} phase={phase} />)
      )}
    </div>
  );
}
